/**
 * Reine, deterministische Berechnungsfunktionen (docs/methodik.md).
 *
 * Eingabe ist eine stündliche UTC-Reihe mit Status je Stunde und dem
 * UTC-Versatz der Standortzeitzone je Stunde. Daraus entstehen:
 *  - eine Tagestabelle je lokalem Kalendertag,
 *  - eine Nachttabelle je Nachtfenster (Datum des Abends),
 *  - Jahreskennzahlen, Hitzeperioden und Referenzmittel.
 * Werte werden intern nicht gerundet.
 */
import { DAY_MS, HOUR_MS, dayIndexMonth, dayIndexToIso, dayIndexYear } from '../time/tz';
import {
  HourStatus,
  type AnalysisConfig,
  type DayRow,
  type HeatEvent,
  type HourPoint,
  type MetricKey,
  type NightRow,
  type ReferenceSummary,
  type YearMetrics,
} from '../domain/types';

export interface SeriesInput {
  t0Ms: number;
  /** Temperatur in °C; nur bei Status Ok aussagekräftig. */
  values: Float64Array;
  status: Uint8Array;
  /** UTC-Versatz der Ortszeit in Minuten je Stunde. */
  offsets: Int16Array;
  /** Konstanter Summand je Stunde (Höhenkorrektur) in K. */
  correctionK: number;
}

export type NightParams = Pick<
  AnalysisConfig,
  'nightStartHour' | 'nightEndHour' | 'coolingRef' | 'coolingMinDelta' | 'coolingSeasonStartMonth' | 'coolingSeasonEndMonth'
>;

export interface DailyTable {
  firstDay: number;
  n: number;
  expected: Uint16Array;
  ok: Uint16Array;
  notLoaded: Uint16Array;
  bad: Uint16Array;
  conflict: Uint16Array;
  edge: Uint8Array;
  sum: Float64Array;
  min: Float64Array;
  max: Float64Array;
}

export interface NightTable {
  firstNight: number;
  n: number;
  expected: Uint16Array;
  ok: Uint16Array;
  notLoaded: Uint16Array;
  bad: Uint16Array;
  edge: Uint8Array;
  min: Float64Array;
  coolHours: Uint16Array;
  coolKh: Float64Array;
}

function wallOf(input: SeriesInput, i: number): number {
  return input.t0Ms + i * HOUR_MS + input.offsets[i] * 60_000;
}

/** Tagestabelle: lokale Kalendertage mit Mittel, Minimum und Maximum. */
export function buildDailyTable(input: SeriesInput): DailyTable {
  const H = input.values.length;
  const firstDay = Math.floor(wallOf(input, 0) / DAY_MS) - 1;
  const lastDay = Math.floor(wallOf(input, H - 1) / DAY_MS) + 1;
  const n = lastDay - firstDay + 1;
  const t: DailyTable = {
    firstDay,
    n,
    expected: new Uint16Array(n),
    ok: new Uint16Array(n),
    notLoaded: new Uint16Array(n),
    bad: new Uint16Array(n),
    conflict: new Uint16Array(n),
    edge: new Uint8Array(n),
    sum: new Float64Array(n),
    min: new Float64Array(n).fill(Infinity),
    max: new Float64Array(n).fill(-Infinity),
  };
  const corr = input.correctionK;
  for (let i = 0; i < H; i++) {
    const slot = Math.floor(wallOf(input, i) / DAY_MS) - firstDay;
    t.expected[slot]++;
    const st = input.status[i];
    if (st === HourStatus.Ok) {
      const v = input.values[i] + corr;
      t.ok[slot]++;
      t.sum[slot] += v;
      if (v < t.min[slot]) t.min[slot] = v;
      if (v > t.max[slot]) t.max[slot] = v;
    } else if (st === HourStatus.NotLoaded) {
      t.notLoaded[slot]++;
    } else {
      t.bad[slot]++;
      if (st === HourStatus.Conflict) t.conflict[slot]++;
    }
    if (i === 0 || i === H - 1) t.edge[slot] = 1;
  }
  return t;
}

function inWindow(minuteOfDay: number, startH: number, endH: number): boolean {
  return minuteOfDay >= startH * 60 || minuteOfDay < endH * 60;
}

function monthInSeason(month: number, start: number, end: number): boolean {
  return start <= end ? month >= start && month <= end : month >= start || month <= end;
}

/**
 * Nachttabelle für ein Fenster über Mitternacht (Start 18–23 Uhr, Ende
 * 4–9 Uhr), halboffen [Start, Ende). Zuordnung zum Datum des Abends.
 */
export function buildNightTable(input: SeriesInput, p: NightParams): NightTable {
  const H = input.values.length;
  const firstNight = Math.floor(wallOf(input, 0) / DAY_MS) - 2;
  const lastNight = Math.floor(wallOf(input, H - 1) / DAY_MS) + 1;
  const n = lastNight - firstNight + 1;
  const t: NightTable = {
    firstNight,
    n,
    expected: new Uint16Array(n),
    ok: new Uint16Array(n),
    notLoaded: new Uint16Array(n),
    bad: new Uint16Array(n),
    edge: new Uint8Array(n),
    min: new Float64Array(n).fill(Infinity),
    coolHours: new Uint16Array(n),
    coolKh: new Float64Array(n),
  };
  const corr = input.correctionK;
  const startMin = p.nightStartHour * 60;
  for (let i = 0; i < H; i++) {
    const wall = wallOf(input, i);
    const day = Math.floor(wall / DAY_MS);
    const minute = (wall - day * DAY_MS) / 60_000;
    if (!inWindow(minute, p.nightStartHour, p.nightEndHour)) continue;
    const evening = minute >= startMin ? day : day - 1;
    const slot = evening - firstNight;
    t.expected[slot]++;
    if (i === 0 || i === H - 1) t.edge[slot] = 1;
    const st = input.status[i];
    if (st === HourStatus.Ok) {
      const v = input.values[i] + corr;
      t.ok[slot]++;
      if (v < t.min[slot]) t.min[slot] = v;
      const diff = p.coolingRef - v;
      if (diff >= p.coolingMinDelta) {
        t.coolHours[slot]++;
        t.coolKh[slot] += diff;
      }
    } else if (st === HourStatus.NotLoaded) {
      t.notLoaded[slot]++;
    } else {
      t.bad[slot]++;
    }
  }
  return t;
}

export const dayValid = (d: DailyTable, s: number) => d.expected[s] > 0 && !d.edge[s] && d.ok[s] === d.expected[s];
export const dayLoaded = (d: DailyTable, s: number) => d.expected[s] > 0 && !d.edge[s] && d.notLoaded[s] === 0;
export const nightValid = (t: NightTable, s: number) => t.expected[s] > 0 && !t.edge[s] && t.ok[s] === t.expected[s];
export const nightLoaded = (t: NightTable, s: number) => t.expected[s] > 0 && !t.edge[s] && t.notLoaded[s] === 0;

export interface HeatParams {
  hotDayThreshold: number;
  heatPeriodMinDays: number;
}

/**
 * Hitzeperioden: mindestens `heatPeriodMinDays` aufeinanderfolgende gültige
 * lokale Tage mit Stundenmaximum ≥ Schwelle. Ungültige Tage unterbrechen.
 * Nicht existierende Kalendertage (z. B. übersprungener Tag bei Zonenwechsel)
 * werden übergangen. Keine Mehrfachzählung überlappender Fenster.
 */
export function findHeatEvents(d: DailyTable, p: HeatParams): HeatEvent[] {
  const events: HeatEvent[] = [];
  let runStart = -1;
  let runDays = 0;
  let runMax = -Infinity;
  let lastSlot = -1;
  const close = () => {
    if (runDays >= p.heatPeriodMinDays) {
      events.push({
        startDate: dayIndexToIso(d.firstDay + runStart),
        endDate: dayIndexToIso(d.firstDay + lastSlot),
        days: runDays,
        maxT: runMax,
      });
    }
    runStart = -1;
    runDays = 0;
    runMax = -Infinity;
  };
  for (let s = 0; s < d.n; s++) {
    if (d.expected[s] === 0) continue;
    if (dayValid(d, s) && d.max[s] >= p.hotDayThreshold) {
      if (runStart < 0) runStart = s;
      runDays++;
      if (d.max[s] > runMax) runMax = d.max[s];
      lastSlot = s;
    } else {
      close();
    }
  }
  close();
  return events;
}

function emptyYear(year: number): YearMetrics {
  return {
    year,
    loaded: true,
    complete: true,
    daysExpected: 0,
    daysValid: 0,
    nightsExpected: 0,
    nightsValid: 0,
    seasonNightsExpected: 0,
    seasonNightsValid: 0,
    seasonNightHours: 0,
    hoursMissing: 0,
    hoursConflict: 0,
    hdd: 0,
    cdd: 0,
    hotDays: 0,
    heatEvents: 0,
    heatDays: 0,
    heatLongest: 0,
    warmNights: 0,
    coolHours: 0,
    coolKh: 0,
    tMean: null,
    tropicalNights: 0,
    veryHotDays: 0,
    txx: null,
  };
}

/** Feste Standardschwellen, wie sie Klimaszenarien-Datensätze verwenden. */
export const TROPICAL_NIGHT_MIN = 20;
export const VERY_HOT_DAY_MAX = 35;

export type YearParams = Pick<
  AnalysisConfig,
  'hddBase' | 'cddBase' | 'hotDayThreshold' | 'heatPeriodMinDays' | 'warmNightThreshold' | 'coolingSeasonStartMonth' | 'coolingSeasonEndMonth'
>;

/** Jahreskennzahlen für alle Jahre im Bereich [fromYear, toYear]. */
export function computeYears(
  d: DailyTable,
  nt: NightTable,
  p: YearParams,
  fromYear: number,
  toYear: number,
): YearMetrics[] {
  const years = new Map<number, YearMetrics & { _tSum: number }>();
  const get = (y: number) => {
    let m = years.get(y);
    if (!m) {
      m = { ...emptyYear(y), _tSum: 0 };
      years.set(y, m);
    }
    return m;
  };
  for (let y = fromYear; y <= toYear; y++) get(y);

  for (let s = 0; s < d.n; s++) {
    if (d.expected[s] === 0) continue;
    const idx = d.firstDay + s;
    const y = dayIndexYear(idx);
    if (y < fromYear || y > toYear) continue;
    const m = get(y);
    m.daysExpected++;
    m.hoursMissing += d.bad[s] - d.conflict[s];
    m.hoursConflict += d.conflict[s];
    if (!dayLoaded(d, s)) m.loaded = false;
    if (!dayValid(d, s)) {
      m.complete = false;
      continue;
    }
    m.daysValid++;
    const mean = d.sum[s] / d.expected[s];
    m._tSum += mean;
    m.hdd += Math.max(0, p.hddBase - mean);
    m.cdd += Math.max(0, mean - p.cddBase);
    if (d.max[s] >= p.hotDayThreshold) m.hotDays++;
    if (d.min[s] >= TROPICAL_NIGHT_MIN) m.tropicalNights++;
    if (d.max[s] >= VERY_HOT_DAY_MAX) m.veryHotDays++;
    if (m.txx === null || d.max[s] > m.txx) m.txx = d.max[s];
  }

  for (let s = 0; s < nt.n; s++) {
    if (nt.expected[s] === 0) continue;
    const idx = nt.firstNight + s;
    const y = dayIndexYear(idx);
    if (y < fromYear || y > toYear) continue;
    const m = get(y);
    const inSeason = monthInSeason(dayIndexMonth(idx), p.coolingSeasonStartMonth, p.coolingSeasonEndMonth);
    m.nightsExpected++;
    if (inSeason) m.seasonNightsExpected++;
    if (!nightLoaded(nt, s)) m.loaded = false;
    if (!nightValid(nt, s)) {
      m.complete = false;
      continue;
    }
    m.nightsValid++;
    if (nt.min[s] >= p.warmNightThreshold) m.warmNights++;
    if (inSeason) {
      m.seasonNightsValid++;
      m.seasonNightHours += nt.ok[s];
      m.coolHours += nt.coolHours[s];
      m.coolKh += nt.coolKh[s];
    }
  }

  for (const ev of findHeatEvents(d, p)) {
    const startIdx = Math.floor(Date.parse(ev.startDate) / DAY_MS);
    const sy = dayIndexYear(startIdx);
    if (sy >= fromYear && sy <= toYear) {
      const m = get(sy);
      m.heatEvents++;
      if (ev.days > m.heatLongest) m.heatLongest = ev.days;
    }
    // Enthaltene Tage dem jeweiligen Kalenderjahr zuordnen.
    const endIdx = Math.floor(Date.parse(ev.endDate) / DAY_MS);
    for (let idx = startIdx; idx <= endIdx; idx++) {
      const s = idx - d.firstDay;
      if (d.expected[s] === 0) continue;
      const y = dayIndexYear(idx);
      if (y >= fromYear && y <= toYear) get(y).heatDays++;
    }
  }

  const out: YearMetrics[] = [];
  for (const m of [...years.values()].sort((a, b) => a.year - b.year)) {
    const { _tSum, ...rest } = m;
    rest.tMean = m.daysValid > 0 ? _tSum / m.daysValid : null;
    if (m.daysExpected === 0) {
      rest.loaded = false;
      rest.complete = false;
    }
    out.push(rest);
  }
  return out;
}

export const METRIC_KEYS: MetricKey[] = [
  'hdd',
  'cdd',
  'warmNights',
  'coolHours',
  'coolKh',
  'hotDays',
  'heatEvents',
  'heatDays',
  'heatLongest',
  'tMean',
  'tropicalNights',
  'veryHotDays',
  'txx',
];

/** Letztes vollständiges Jahr im gewählten Zeitraum; Hauptansicht und Sensitivität nutzen dieselbe Regel. */
export function lastCompleteInPeriod(years: YearMetrics[], start: number, end: number): YearMetrics | null {
  for (let k = years.length - 1; k >= 0; k--) {
    const y = years[k];
    if (y.complete && y.year >= start && y.year <= end) return y;
  }
  return null;
}

/**
 * Referenzmittel über vollständige Jahre. Unter 30 gültigen Jahren
 * «Teilreferenz», unter 20 keine zusammenfassende Kennzahl.
 */
export function summarizeReference(years: YearMetrics[], start: number, end: number, onlyYears?: Set<number>): ReferenceSummary {
  const total = end - start + 1;
  const valid: number[] = [];
  const excluded: number[] = [];
  const sums: Partial<Record<MetricKey, number>> = {};
  for (let y = start; y <= end; y++) {
    const m = years.find((x) => x.year === y);
    const ok = m && m.complete && (!onlyYears || onlyYears.has(y));
    if (!ok) {
      excluded.push(y);
      continue;
    }
    valid.push(y);
    for (const k of METRIC_KEYS) {
      const v = m![k];
      if (typeof v === 'number') sums[k] = (sums[k] ?? 0) + v;
    }
  }
  const status = valid.length >= total ? 'voll' : valid.length >= 20 ? 'teil' : 'ungenuegend';
  const means: Partial<Record<MetricKey, number>> = {};
  if (status !== 'ungenuegend') {
    for (const k of METRIC_KEYS) if (sums[k] !== undefined) means[k] = sums[k]! / valid.length;
  }
  return { start, end, yearsTotal: total, yearsValid: valid, yearsExcluded: excluded, status, means };
}

/** Relative Veränderung; bei Referenz 0 nicht definiert (null). */
export function relativeChange(value: number, ref: number): number | null {
  if (ref === 0) return null;
  return (value - ref) / Math.abs(ref);
}

/** Gemeinsame vollständige Jahre mehrerer Standorte. */
export function commonCompleteYears(results: YearMetrics[][], from: number, to: number): number[] {
  const out: number[] = [];
  for (let y = from; y <= to; y++) {
    if (results.every((ys) => ys.some((m) => m.year === y && m.complete))) out.push(y);
  }
  return out;
}

/** Tages- und Nachtzeilen eines Jahres für die Detailansicht. */
export function yearRows(d: DailyTable, nt: NightTable, p: NightParams, year: number): { days: DayRow[]; nights: NightRow[] } {
  const days: DayRow[] = [];
  for (let s = 0; s < d.n; s++) {
    const idx = d.firstDay + s;
    if (d.expected[s] === 0 || dayIndexYear(idx) !== year) continue;
    const valid = dayValid(d, s);
    const has = d.ok[s] > 0;
    days.push({
      date: dayIndexToIso(idx),
      valid,
      loaded: dayLoaded(d, s),
      hours: d.ok[s],
      expected: d.expected[s],
      mean: valid ? d.sum[s] / d.expected[s] : null,
      min: has ? d.min[s] : null,
      max: has ? d.max[s] : null,
    });
  }
  const nights: NightRow[] = [];
  for (let s = 0; s < nt.n; s++) {
    const idx = nt.firstNight + s;
    if (nt.expected[s] === 0 || dayIndexYear(idx) !== year) continue;
    const valid = nightValid(nt, s);
    nights.push({
      date: dayIndexToIso(idx),
      valid,
      hours: nt.ok[s],
      expected: nt.expected[s],
      min: valid ? nt.min[s] : null,
      coolHours: valid ? nt.coolHours[s] : 0,
      coolKh: valid ? nt.coolKh[s] : 0,
      inSeason: monthInSeason(dayIndexMonth(idx), p.coolingSeasonStartMonth, p.coolingSeasonEndMonth),
    });
  }
  return { days, nights };
}

/** Stundenwerte für einen lokalen Datumsbereich [fromIso, toIso] inklusive. */
export function hourPoints(input: SeriesInput, fromIso: string, toIso: string, p: Pick<AnalysisConfig, 'nightStartHour' | 'nightEndHour'>): HourPoint[] {
  const from = Math.floor(Date.parse(fromIso) / DAY_MS);
  const to = Math.floor(Date.parse(toIso) / DAY_MS);
  const out: HourPoint[] = [];
  for (let i = 0; i < input.values.length; i++) {
    const wall = wallOf(input, i);
    const day = Math.floor(wall / DAY_MS);
    if (day < from || day > to) continue;
    const st = input.status[i];
    const minute = (wall - day * DAY_MS) / 60_000;
    out.push({
      utc: new Date(input.t0Ms + i * HOUR_MS).toISOString().replace('.000Z', 'Z'),
      local: new Date(wall).toISOString().slice(0, 16),
      t: st === HourStatus.Ok ? input.values[i] + input.correctionK : null,
      status: st === HourStatus.Ok ? 'ok' : st === HourStatus.Missing ? 'fehlend' : st === HourStatus.Conflict ? 'widerspruch' : 'nicht geladen',
      inNight: inWindow(minute, p.nightStartHour, p.nightEndHour),
    });
  }
  return out;
}
